import { useQueryClient } from "@tanstack/react-query";
import { PlusIcon } from "@heroicons/react/outline";
import { useMemo } from "react";

import CreateInviteForm from "../CreateInviteForm";

import { ENDPOINTS } from "@/api/application-api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Workspace } from "@/types/workspaces";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { useOrganizationSubscriptionData } from "@/providers";
import { getActiveOrganizationMembers, useActiveOrgMembers } from "@/queries/organization-members";
import { useCurrentUser } from "@/queries/users";
import { useGetWorkspaceInvites } from "@/queries/workspace";

export const InviteMember = ({
  open,
  setOpen,
  hasTrigger = true,
  workspace,
  isWorkspacesTable = false,
  organizationId,
  email,
  buttonLabel,
  excludeWorkspaceIds,
  onInviteCreate,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  hasTrigger?: boolean;
  workspace?: Workspace | null;
  isWorkspacesTable?: boolean;
  organizationId: number;
  email?: string | null;
  buttonLabel?: string;
  excludeWorkspaceIds?: number[];
  onInviteCreate?: () => void;
}) => {
  const { isPLAdmin, subscription } = useOrganizationSubscriptionData();
  const { data: user } = useCurrentUser();
  const { data: orgMembersData } = useActiveOrgMembers({
    organization_id: organizationId,
    perPage: 250,
    page: 1,
  });
  const { data: workspaceInvites } = useGetWorkspaceInvites({
    organization_id: organizationId,
    status: "pending",
  });
  const queryClient = useQueryClient();

  const isNewMemberContext = (buttonLabel ?? "New Member") === "New Member";

  const hasReachedUserLimit = useMemo(() => {
    if (!isNewMemberContext) {
      return false;
    }
    const maxUsers = subscription?.max_users === null ? null : (subscription?.max_users ?? 5);
    if (typeof maxUsers !== "number" || maxUsers <= 0) {
      return false;
    }
    if (orgMembersData === undefined || workspaceInvites === undefined) {
      return false;
    }
    const totalMembers = getActiveOrganizationMembers(orgMembersData?.items).length;
    const pendingInvites = workspaceInvites?.items?.length ?? 0;
    return totalMembers + pendingInvites >= maxUsers;
  }, [isNewMemberContext, subscription?.max_users, orgMembersData, workspaceInvites]);

  const isOrgOwner = user?.memberships.organizations.find(
    org => org.organization_id === organizationId,
  )?.is_owner;

  if (isWorkspacesTable && !isOrgOwner) {
    return null;
  }

  if (!isOrgOwner && !isPLAdmin) {
    const text = "You are not the owner of this organization.";
    return (
      <BasicTooltip content={text}>
        <span className="flex items-center">
          <Button variant="outline" disabled>
            <PlusIcon className="mr-2 h-4 w-4" /> New Member
          </Button>
        </span>
      </BasicTooltip>
    );
  }

  const triggerLabel = buttonLabel ?? "New Member";
  const limitReachedTooltip =
    "You have reached your user limit. Please upgrade your plan to add new members.";

  const getTriggerElement = () => {
    if (isWorkspacesTable) {
      return null;
    }

    const isDisabled = hasReachedUserLimit;

    const button = (
      <Button variant="outline" disabled={isDisabled} onClick={() => !isDisabled && setOpen(true)}>
        <PlusIcon className="mr-2 h-4 w-4" /> {triggerLabel}
      </Button>
    );

    if (isDisabled && isNewMemberContext) {
      return (
        <BasicTooltip content={limitReachedTooltip}>
          <span className="inline-flex">{button}</span>
        </BasicTooltip>
      );
    }

    if (hasTrigger) {
      return <DialogTrigger asChild>{button}</DialogTrigger>;
    }
    return button;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {getTriggerElement()}
      <DialogContent className="!w-max min-w-[450px] !max-w-[calc(100vw-2rem)] md:!w-max">
        <DialogHeader>
          <DialogTitle>Invite to Workspace</DialogTitle>
        </DialogHeader>
        <CreateInviteForm
          onSuccess={() => {
            setOpen(false);
            queryClient.invalidateQueries({
              queryKey: ["workspace-members"],
            });
            queryClient.invalidateQueries({
              queryKey: [ENDPOINTS.organization_members],
            });
            onInviteCreate?.();
          }}
          organizationId={organizationId}
          workspace={workspace}
          email={email}
          excludeWorkspaceIds={excludeWorkspaceIds}
        />
      </DialogContent>
    </Dialog>
  );
};
